import { useEffect, useState } from "react";
import {
    FaLocationArrow,
    FaSave,
    FaList,
    FaArrowLeft,
} from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import FullPageSpinner from "../../components/FullPageSpinner";
import { useModal } from "../../context/ConfirmModalContext";
import { useToast } from "../../context/ToastContext";
import { sendRequest, serverError } from "../../utils/api";
import { initAllStores } from "../../utils/cache";
import Site from "../../models/Site";

export default function CreateSitePage() {
    const navigate = useNavigate();
    const { openModal, closeModal } = useModal();
    const { showToast } = useToast();

    const { id } = useParams<{ id: string }>();

    const [loading, setLoading] = useState<boolean>(false);

    const [form, setForm] = useState({
        name: "",
        address: "",
        lat: "",
        lon: "",
        radius: "100",
    });


    useEffect(() => {
        if (id) {
            const siteStr = localStorage.getItem("site");

            const site = JSON.parse(siteStr ?? "{}");

            setForm({
                name: site.name,
                address: site.address,
                lat: site.lat,
                lon: site.lon,
                radius: site.radius,
            })
        }
    }, [])

    const getCurrentLocation = () => {
        setLoading(true);
        if (!navigator.geolocation) {
            alert("Geolocation is not supported.");
            return;
        }

        try {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setForm((prev) => ({
                        ...prev,
                        lat: position.coords.latitude.toString(),
                        lon: position.coords.longitude.toString(),
                    }));
                },
                () => {
                    alert("Unable to retrieve your location.");
                },
                {
                    enableHighAccuracy: true,
                }
            );
        } catch {

        } finally {
            setLoading(false);
        }
    };


    const createBusinessSite = () => {
        const missingFields: string[] = [];

        if (!form.name) missingFields.push("Site Name");
        if (!form.address) missingFields.push("Site Address");
        if (!form.lat) missingFields.push("Latitude");
        if (!form.lon) missingFields.push("Longitude");
        if (!form.radius) missingFields.push("Site Radius");

        if (missingFields.length > 0) {
            return openModal(
                "Missing Fields",
                `Please fill in: ${missingFields.join(", ")}`,
                closeModal, false, "Close"
            );
        }
        const msg = (
            `You’re about to ${id ? "update" : "create a new"} business site. `
        );

        return openModal(
            `Confirm Site ${id ? "Update" : "Creation"}`,
            msg + "Do you want to continue?",
            async () => {
                setLoading(true);
                try {
                    const cache = await initAllStores();

                    const user = await cache.getBy("data", { id: "user", });

                    if (user.role === "staff")
                        return alert(
                            "You don't have permission to perform this action. " +
                            "Please contact your administrator."
                        );

                    const data = {
                        lat: parseFloat(form.lat),
                        lon: parseFloat(form.lon),
                        radius: parseInt(form.radius),
                        address: form.address,
                        name: form?.name?.trim()?.toLowerCase(),
                    };


                    const url = id ? `/sites/${id}/update` : "/sites";
                    const method = id ? "PUT" : "POST";
                    await sendRequest(method, url, data);
                    return showToast(
                        `Business Site ${id ? "Updated" : "Created"} Successfully.`,
                        { type: "success" }
                    );

                } catch (err: any) {

                    if (err.status === 409)
                        return showToast(
                            serverError(err),
                            { type: "error" }
                        );
                    else if (err.status === 403)
                        return alert(serverError(err));
                } finally {
                    setLoading(false);
                }
            }, true, `Yes, ${id ? "Update" : "Create"}`, "Cancel"
        );
    };


    return (
        <div className="min-h-screen bg-gray-50">

            {/* Header */}
            <div className="bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">

                    <FullPageSpinner show={loading} />

                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition"
                        >
                            {FaArrowLeft({ className: "text-gray-600 text-lg" })}
                        </button>

                        <div>
                            <div className="flex items-center justify-between">
                                <h1 className="text-2xl font-bold">
                                    {!id ? "Create" : "Update"}  Site
                                </h1>

                                {/* <button
                                    onClick={() => navigate("/settings/sites")}
                                    className="flex sm:hidden items-center justify-center w-10 h-10 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white transition"
                                >
                                    {FaList({})}
                                </button> */}
                            </div>
                            {/* <p className="text-gray-500">
                                Configure attendance locations and GPS radius.
                            </p> */}
                        </div>
                    </div>
                    {/* <div className="flex items-center gap-2">
                        <button
                            onClick={() => navigate("/settings/sites")}
                            className="hidden sm:flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-3 rounded-xl transition"
                        >
                            {FaList({})}
                            View All Sites
                        </button>


                    </div> */}

                </div>
            </div>

            {/* Body */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                <div className="bg-white rounded-2xl border shadow-sm p-6 md:p-8">
                    <div className="space-y-6">

                        <div>
                            <label className="block mb-2 font-semibold">
                                Site Name
                            </label>

                            <input
                                className="w-full border rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 outline-none capitalize"
                                placeholder="Head Office"
                                value={form.name}
                                onChange={(e) =>
                                    setForm({ ...form, name: e.target.value })
                                }
                                required={true}
                            />
                        </div>

                        <div>
                            <label className="block mb-2 font-semibold">
                                Address
                            </label>

                            <textarea
                                rows={3}
                                className="w-full border rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 outline-none"
                                placeholder="Enter site address..."
                                value={form.address}
                                onChange={(e) =>
                                    setForm({ ...form, address: e.target.value })
                                }
                                required={true}
                            />
                        </div>

                        <div className="grid md:grid-cols-2 gap-5">

                            <div>
                                <label className="block mb-2 font-semibold">
                                    Latitude
                                </label>

                                <input
                                    className="w-full border rounded-xl px-4 py-3"
                                    placeholder="4.97842"
                                    value={form.lat}
                                    onChange={(e) => {
                                        const value = e.target.value;

                                        // Allow only digits and one decimal point
                                        if (/^\d*\.?\d*$/.test(value)) {
                                            setForm({
                                                ...form,
                                                lat: value,
                                            });
                                        }
                                    }}
                                />
                            </div>

                            <div>
                                <label className="block mb-2 font-semibold">
                                    Longitude
                                </label>

                                <input
                                    className="w-full border rounded-xl px-4 py-3"
                                    placeholder="7.92651"
                                    value={form.lon}
                                    onChange={(e) => {
                                        const value = e.target.value;

                                        if (/^\d*\.?\d*$/.test(value)) {
                                            setForm({
                                                ...form,
                                                lon: value,
                                            });
                                        }
                                    }}
                                />
                            </div>

                        </div>

                        <button
                            type="button"
                            onClick={getCurrentLocation}
                            className="flex items-center gap-3 bg-green-600 hover:bg-green-700 text-white px-5 py-3 rounded-xl transition"
                        >
                            {FaLocationArrow({})}
                            Use Current Location
                        </button>

                        <div>
                            <label className="block mb-2 font-semibold">
                                Attendance Radius
                            </label>

                            <select
                                value={form.radius}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        radius: e.target.value,
                                    })
                                }
                                className="w-full border rounded-xl px-4 py-3"
                            >
                                <option value="25">25 metres</option>
                                <option value="50">50 metres</option>
                                <option value="75">75 metres</option>
                                <option value="100">100 metres</option>
                                <option value="150">150 metres</option>
                                <option value="200">200 metres</option>
                                <option value="300">300 metres</option>
                                <option value="500">500 metres</option>
                            </select>

                            <p className="text-sm text-gray-500 mt-2">
                                Staff must be within this radius to clock in or clock out.
                            </p>
                        </div>

                        <button
                            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-4 rounded-2xl text-base font-bold tracking-wide flex items-center justify-center gap-3 transition-all duration-200"
                            onClick={createBusinessSite}
                        >
                            {FaSave({ className: "text-xl" })}
                            <span>{!id ? "Save" : "Update"} Site</span>
                        </button>

                    </div>
                </div>
            </div>
        </div>
    );
}